/**
 * ErrorBoundary — Reusable error boundary for page sections and routes.
 *
 * Features:
 * - Catches React render errors without crashing the whole app
 * - Auto-retries up to 3 times (page-level) or 1 time (section-level) after 3s delay
 * - Shows styled error card with "Reload" button after max retries exhausted
 * - Logs errors to console (swap for Sentry when ready)
 */

import { Component } from 'react';
import { captureError } from '../lib/sentry';
import { generateErrorId, sanitizeErrorMessage } from '../utils/safeSupabase';
import ErrorState from './states/ErrorState';

/** Page-level boundaries retry 3 times; section-level retry once. */
const PAGE_MAX_RETRIES = 3;
const SECTION_MAX_RETRIES = 1;
const RETRY_DELAY_MS = 3000;

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorId: null, retryCount: 0, autoRetrying: false };
    this._retryTimer = null;
  }

  static getDerivedStateFromError(error) {
    // Phase 13.2 — assign a stable errorId at catch time so the user-facing
    // ref + Sentry capture both reference the same id.
    return { hasError: true, error, errorId: generateErrorId() };
  }

  componentDidCatch(error, info) {
    const label = this.props.label || 'Component';
    const errorId = this.state.errorId || generateErrorId();
    console.error(`[ErrorBoundary:${label}] retryCount=${this.state.retryCount} errorId=${errorId}`, error, info?.componentStack);
    captureError(error, { label, errorId, retryCount: this.state.retryCount, componentStack: info?.componentStack });

    // Stale chunk after deployment — auto-reload the page once
    if (error?.message?.includes('Failed to fetch dynamically imported module') ||
        error?.message?.includes('Loading chunk') ||
        error?.message?.includes('Loading CSS chunk')) {
      const lastReload = sessionStorage.getItem('juuni_chunk_reload');
      const now = Date.now();
      // Only auto-reload once per 30 seconds to prevent infinite reload loops
      if (!lastReload || now - parseInt(lastReload) > 30000) {
        sessionStorage.setItem('juuni_chunk_reload', String(now));
        console.warn('[ErrorBoundary] Stale chunks detected — reloading page');
        window.location.reload();
        return;
      }
    }

    this._scheduleAutoRetry();
  }

  componentWillUnmount() {
    this._clearRetryTimer();
  }

  _getMaxRetries() {
    return this.props.section ? SECTION_MAX_RETRIES : PAGE_MAX_RETRIES;
  }

  _clearRetryTimer() {
    if (this._retryTimer) {
      clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
  }

  _scheduleAutoRetry() {
    this._clearRetryTimer();

    const maxRetries = this._getMaxRetries();
    if (this.state.retryCount >= maxRetries) return;

    this.setState({ autoRetrying: true });

    this._retryTimer = setTimeout(() => {
      this._retryTimer = null;
      this.setState((prev) => ({
        hasError: false,
        error: null,
        retryCount: prev.retryCount + 1,
        autoRetrying: false,
      }));
    }, RETRY_DELAY_MS);
  }

  /** Manual retry resets the counter so the user gets fresh auto-retry attempts. */
  handleRetry = () => {
    this._clearRetryTimer();
    this.setState({ hasError: false, error: null, retryCount: 0, autoRetrying: false });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { section, label } = this.props;
    const { retryCount, autoRetrying } = this.state;
    const maxRetries = this._getMaxRetries();
    const retriesExhausted = retryCount >= maxRetries;
    const title = label ? `${label} Error` : 'Something went wrong';

    // Section-level: compact inline card
    if (section) {
      return (
        <div style={{
          padding: 16, margin: '8px 0', borderRadius: 8,
          background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)',
          fontFamily: 'var(--font-mono, monospace)',
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-error, #ef4444)', marginBottom: 6 }}>
            {title}
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-muted, #888)', marginBottom: 10 }}>
            {autoRetrying
              ? `Retrying... (attempt ${retryCount + 1} of ${maxRetries})`
              : retriesExhausted
                ? 'This section failed to recover. Other sections are unaffected.'
                : 'This section encountered an error. Other sections are unaffected.'}
          </div>
          {retriesExhausted && (
            <button onClick={this.handleRetry} style={{
              padding: '4px 12px', fontSize: 10, fontFamily: 'var(--font-mono, monospace)',
              background: 'var(--accent-teal)', color: 'var(--bg)', border: 'none', borderRadius: 4, cursor: 'pointer',
            }}>Retry</button>
          )}
        </div>
      );
    }

    // Page-level: render ErrorState component (Phase 13.2.3 / 13.3.3
    // architectural alignment per audit-fix 2026-04-29). DEV-only stack
    // panel rendered alongside in development for debugging.
    const userMessage = autoRetrying
      ? `Retrying... (attempt ${retryCount + 1} of ${maxRetries})`
      : retriesExhausted
        ? 'Something went wrong. Please try again or reload the page.'
        : 'An unexpected error occurred. Your data is safe — this is a display issue.';

    return (
      <div style={{
        padding: 40, fontFamily: 'var(--font-mono, monospace)',
        maxWidth: 640, margin: '80px auto',
      }}>
        <h2 style={{
          fontSize: 16, fontWeight: 600,
          color: 'var(--text-error, #ef4444)',
          marginBottom: 16, textAlign: 'center',
          fontFamily: 'var(--font-mono, monospace)',
          letterSpacing: '0.04em',
        }}>
          {title}
        </h2>

        {retriesExhausted ? (
          <ErrorState
            message={userMessage}
            errorId={this.state.errorId}
            contactSupport={true}
            ariaLabel={title}
            action={
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={this.handleRetry} style={{
                  padding: '8px 20px', fontSize: 12,
                  fontFamily: 'var(--font-mono, monospace)',
                  background: 'var(--accent-teal, #5DCAA5)', color: 'var(--bg, #14110c)',
                  border: 'none', borderRadius: 6,
                  cursor: 'pointer', fontWeight: 500,
                  minHeight: 36, letterSpacing: '0.02em',
                }}>Try Again</button>
                <button onClick={() => window.location.reload()} style={{
                  padding: '8px 20px', fontSize: 12,
                  fontFamily: 'var(--font-mono, monospace)',
                  background: 'transparent', color: 'var(--text-muted, #6b6254)',
                  border: '1px solid var(--border, rgba(0,0,0,0.10))',
                  borderRadius: 6, cursor: 'pointer',
                  minHeight: 36, letterSpacing: '0.02em',
                }}>Reload Page</button>
              </div>
            }
          />
        ) : (
          // Auto-retrying state — show ErrorState with retry message; no buttons (auto-retry handles it)
          <ErrorState
            message={userMessage}
            errorId={this.state.errorId}
            contactSupport={false}
            ariaLabel={title}
          />
        )}

        {/* Phase 13.2 — DEV-only inline stack for debugging. Production users
            see only the ErrorState message + ref id (OWASP A10:2025). */}
        {this.state.error && import.meta.env.DEV && (
          <pre style={{
            whiteSpace: 'pre-wrap', fontSize: 10, textAlign: 'left',
            background: 'rgba(0,0,0,0.05)', color: 'var(--text-error, #ef4444)',
            padding: 16, borderRadius: 8, marginTop: 16,
            maxHeight: 200, overflow: 'auto',
          }}>
            {`[DEV ONLY — NEVER SHOWN IN PRODUCTION]\n`}
            {sanitizeErrorMessage(this.state.error, { errorId: this.state.errorId })}{'\n\n'}
            {`Raw (dev): ${this.state.error.message}${'\n'}${this.state.error.stack || ''}`}
          </pre>
        )}
      </div>
    );
  }
}
